import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression } from 'leaflet';

const markerIcon = new L.Icon({
    iconUrl: '/maps/maker.png',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [1, -34],
});

// Výchozí pozice - střed ČR
const DEFAULT_CENTER: LatLngExpression = [49.8, 15.5];
const DEFAULT_ZOOM = 7;
const SELECTED_ZOOM = 17;

interface MapPickerDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (lat: number, lng: number) => void;
    initialGps?: string;
    geocodedObecGps?: [number, number] | null;
}

// Komponenta pro zachycení kliknutí na mapu
const MapClickHandler: React.FC<{
    onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
    useMapEvents({
        click: (e) => {
            const { lat, lng } = e.latlng;
            onLocationSelect(
                Math.round(lat * 1000000) / 1000000,
                Math.round(lng * 1000000) / 1000000
            );
        },
    });
    return null;
};

// Komponenta pro flyTo při změně pozice
const FlyToLocation: React.FC<{ position: LatLngExpression | null; zoom?: number }> = ({ position, zoom = SELECTED_ZOOM }) => {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.flyTo(position, zoom, { animate: true, duration: 0.5 });
        }
    }, [position, map, zoom]);
    return null;
};

// Parse GPS string to coordinates
function parseGpsString(gps: string): [number, number] | null {
    if (!gps) return null;
    const parts = gps.split(',');
    if (parts.length !== 2) return null;

    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());

    if (isNaN(lat) || isNaN(lng)) return null;
    return [lat, lng];
}

const OBEC_ZOOM = 14;

const MapPickerDialog: React.FC<MapPickerDialogProps> = ({
    open,
    onClose,
    onSelect,
    initialGps,
    geocodedObecGps,
}) => {
    const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(null);

    // Reset při otevření dialogu
    useEffect(() => {
        if (open) {
            const initial = initialGps ? parseGpsString(initialGps) : null;
            setSelectedPosition(initial);
        }
    }, [open, initialGps]);

    const handleLocationSelect = (lat: number, lng: number) => {
        setSelectedPosition([lat, lng]);
    };

    const handleConfirm = () => {
        if (selectedPosition) {
            onSelect(selectedPosition[0], selectedPosition[1]);
            onClose();
        }
    };

    const handleCancel = () => {
        setSelectedPosition(null);
        onClose();
    };

    const initialCenter = initialGps ? parseGpsString(initialGps) : null;
    const mapCenter = initialCenter || geocodedObecGps || DEFAULT_CENTER;
    const mapZoom = initialCenter ? SELECTED_ZOOM : geocodedObecGps ? OBEC_ZOOM : DEFAULT_ZOOM;

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Vyberte polohu na mapě</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Klikněte na mapu pro výběr GPS souřadnic.
                </Typography>
                <Box sx={{ height: 450, width: '100%' }}>
                    {open && (
                        <MapContainer
                            center={mapCenter}
                            zoom={mapZoom}
                            style={{ height: '100%', width: '100%', borderRadius: 8 }}
                            attributionControl={false}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution=""
                            />
                            <MapClickHandler onLocationSelect={handleLocationSelect} />
                            {selectedPosition && (
                                <>
                                    <Marker position={selectedPosition} icon={markerIcon} />
                                    <FlyToLocation position={selectedPosition} />
                                </>
                            )}
                        </MapContainer>
                    )}
                </Box>
                {selectedPosition && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Vybrané souřadnice: <strong>{selectedPosition[0]}, {selectedPosition[1]}</strong>
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Zrušit</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!selectedPosition}
                >
                    Potvrdit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MapPickerDialog;
